import FeatureApi from '@/api/Feature'
import type {
  FeatureFetchData,
  FeatureFetchParams,
  FeatureListItemData,
  UserPreferenceBody,
} from '@/api/Feature/types.ts'
import type { Paginated, ParamType } from '@/api/types.ts'
import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  type UseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query'

export function getFeatureQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType,
  options?: { enabled?: boolean }
) {
  return queryOptions({
    queryKey: ['get-initiative-feature', initiativeId, featureId],
    queryFn: () => FeatureApi.get([initiativeId, featureId]),
    ...options,
  })
}

export function useGetFeature(
  initiativeId: ParamType,
  featureId: ParamType,
  options?: { enabled?: boolean }
) {
  return useQuery(getFeatureQueryOptions(initiativeId, featureId, options))
}

export function useFetchFeature(
  initiativeId: ParamType,
  params: FeatureFetchParams = {},
  options?: Omit<
    UseQueryOptions<unknown, unknown, Paginated<FeatureFetchData>, unknown[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    ...options,
    queryKey: ['fetch-feature', initiativeId, params],
    queryFn: () =>
      FeatureApi.fetch([initiativeId], {
        orderBy: 'name',
        orderSort: 'ASC',
        ...params,
      }),
  })
}

export function getChildFeaturesQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType
) {
  return queryOptions({
    queryKey: ['get-child-initiatives', initiativeId, featureId],
    queryFn: () => FeatureApi.fetchChildren([initiativeId, featureId]),
  })
}

export function useGetChildFeatures(
  initiativeId: ParamType,
  featureId: ParamType
) {
  return useQuery(getChildFeaturesQueryOptions(initiativeId, featureId))
}

export function useGetFeatureByLink() {
  return useMutation({
    mutationFn: (key: string) => FeatureApi.fetchByLink(key),
  })
}

export function useFetchLinkedCollects(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType
) {
  return useQuery({
    queryKey: ['fetch-linked-collects', initiativeId, featureId, collectId],
    queryFn: () =>
      FeatureApi.fetchLinkedCollects([initiativeId, featureId, collectId]),
  })
}

export function useSaveUserPreference(
  initiativeId: ParamType,
  featureId: ParamType
) {
  return useMutation({
    mutationFn: (payload: UserPreferenceBody) =>
      FeatureApi.saveUserPreference([initiativeId, featureId], payload),
    onSuccess: async (_, __, ___, context) => {
      await context.client.invalidateQueries({
        queryKey: [
          'get-user-preference',
          initiativeId,
          featureId,
          'collects_selected_projections',
        ],
      })
    },
  })
}

export function useSavePreference(
  initiativeId: ParamType,
  featureId: ParamType
) {
  return useMutation({
    mutationFn: (payload: UserPreferenceBody) =>
      FeatureApi.savePreference([initiativeId, featureId], payload),
    onSuccess: async (_, __, ___, context) => {
      await context.client.invalidateQueries({
        queryKey: [
          'get-preference',
          initiativeId,
          featureId,
          'collects_selected_projections',
        ],
      })
    },
  })
}

export function getUserPreferenceQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType,
  key: string,
  options?: { enabled?: boolean }
) {
  return queryOptions({
    queryKey: ['get-user-preference', initiativeId, featureId, key],
    queryFn: () => FeatureApi.getUserPreference([initiativeId, featureId], key),
    ...options,
  })
}

export function useGetUserPreference(
  initiativeId: ParamType,
  featureId: ParamType,
  key: string,
  options?: { enabled?: boolean }
) {
  return useSuspenseQuery(
    getUserPreferenceQueryOptions(initiativeId, featureId, key, options)
  )
}

export function getPreferenceQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType,
  key: string,
  options?: { enabled?: boolean }
) {
  return queryOptions({
    queryKey: ['get-preference', initiativeId, featureId, key],
    queryFn: () => FeatureApi.getPreference([initiativeId, featureId], key),
    ...options,
  })
}

export function useGetPreference(
  initiativeId: ParamType,
  featureId: ParamType,
  key: string,
  options?: { enabled?: boolean }
) {
  return useSuspenseQuery(
    getPreferenceQueryOptions(initiativeId, featureId, key, options)
  )
}

export function getNextFeatureSummaryPage(
  lastPage: Paginated<FeatureListItemData>,
  allPages: Paginated<FeatureListItemData>[]
) {
  const loadedCount = allPages.reduce(
    (total, page) => total + page.content.length,
    0
  )
  return loadedCount < lastPage.totalRegisters ? allPages.length : undefined
}

export function useFetchFeatureSummary(
  initiativeId: ParamType,
  search: string,
  limit: number
) {
  return useInfiniteQuery({
    queryKey: ['fetch-feature-summary', initiativeId, search, limit],
    queryFn: ({ pageParam }) =>
      FeatureApi.fetchSummary([initiativeId], {
        page: pageParam,
        limit,
        query: search,
      }),
    initialPageParam: 0,
    getNextPageParam: getNextFeatureSummaryPage,
    placeholderData: keepPreviousData,
  })
}
